import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { FormControl, IconButton, InputLabel, MenuItem, Select } from '@mui/material';
import { findIndex } from 'lodash/array';
import { isEmpty } from 'lodash/lang';

import { generateUrlByKey, useCurrentRoute } from '../../../../../root/router';

import { loadLogRecords } from './service';

export default function LogRecordsNavigation() {
  const navigate = useNavigate();
  const currentRoute = useCurrentRoute();
  const [logRecords, setLogRecords] = useState([]);

  useEffect(() => {
    const showLogCheck = currentRoute.key.includes('Patient Studies.Encounter Details.Logs');
    const filterByStatuses = currentRoute.key.includes('Investigator Worklist.Adverse Event Log Review') && [
      'PI_REVIEW'
    ];
    const ssuPatientId = currentRoute.params?.ssuPatientId;
    const patientItemGroupId = currentRoute.params?.patientItemGroupId;
    if (!ssuPatientId || !patientItemGroupId) {
      return;
    }
    loadLogRecords(ssuPatientId, patientItemGroupId, showLogCheck, filterByStatuses).then(function(logRecords) {
      setLogRecords(logRecords);
    });
  }, [currentRoute]);

  const [currentRecordIndex, previousRecordIndex, nextRecordIndex] = useMemo(
    function() {
      const rowId = currentRoute.params?.rowId;
      const itemGroupTemplateVersionId = currentRoute.params?.itemGroupTemplateVersionId;

      if (isEmpty(logRecords) || !rowId || !itemGroupTemplateVersionId) {
        return [-1, -1, -1];
      }

      const currentRecordIndex = findIndex(logRecords, { rowId, itemGroupTemplateVersionId });
      const previousRecordIndex = currentRecordIndex - 1 < 0 ? -1 : currentRecordIndex - 1;
      const nextRecordIndex = currentRecordIndex + 1 > logRecords.length - 1 ? -1 : currentRecordIndex + 1;

      return [currentRecordIndex, previousRecordIndex, nextRecordIndex];
    },
    [logRecords, currentRoute]
  );

  if (isEmpty(logRecords) || currentRecordIndex < 0) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label="Previous"
        title="Previous"
        disabled={previousRecordIndex < 0}
        onClick={function() {
          navigateToRecordByIndex(previousRecordIndex);
        }}
      >
        <NavigateBeforeIcon />
      </IconButton>
      <FormControl sx={{ m: 1, minWidth: 70 }} size="small">
        <InputLabel id="log-record-field-label">Record</InputLabel>
        <Select
          variant="outlined"
          labelId="log-record-field-label"
          id="log-record-field"
          value={currentRecordIndex}
          label="Record"
          onChange={function({ target }) {
            navigateToRecordByIndex(target.value);
          }}
        >
          {logRecords.map(function({ logRowNumber, progress }, recordIndex) {
            return (
              <MenuItem key={logRowNumber} value={recordIndex}>
                №{logRowNumber}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <IconButton
        aria-label="Next"
        title="Next"
        disabled={nextRecordIndex < 0}
        onClick={function() {
          navigateToRecordByIndex(nextRecordIndex);
        }}
      >
        <NavigateNextIcon />
      </IconButton>
    </>
  );

  function navigateToRecordByIndex(recordIndex) {
    const { key, params } = currentRoute;
    const selectedRecord = logRecords[recordIndex];
    if (isEmpty(selectedRecord)) {
      return;
    }

    const { rowId, itemGroupTemplateVersionId } = selectedRecord;
    navigate(
      generateUrlByKey(key, {
        ...params,
        rowId,
        itemGroupTemplateVersionId
      })
    );
  }
}
